import { useState } from 'react';
import {
  APISurveyAttributes,
  ArdoqId,
  PersistedAttributeQuestion,
  PersistedFieldQuestion,
  PersistedReferenceSubQuestion,
  SurveyQuestionType,
} from '@ardoq/api-types';
import { SelectOption } from '@ardoq/select';
import { isDateRangeFieldType } from '@ardoq/date-range';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { Locale } from '@ardoq/locale';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { toId } from '@ardoq/core';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';
import { SurveyReferenceAndDependentQuestionField } from '../surveyQuestionAtoms';
import { SurveyFieldQuestion } from '../FieldQuestion/SurveyFieldQuestion';
import {
  getIdentifierStringFromReferenceAnswerValue,
  setupAttributeQuestionData,
  setupDateRangeFieldQuestionData,
  setupFieldQuestionData,
} from '../questionOperations';
import { isDateRangeValue } from '../questionPredicates';
import { SurveyAttributeQuestion } from '../SurveyAttributeQuestion';
import { SurveyDateRangeFieldQuestion } from '../SurveyDateRangeFieldQuestion';
import { SurveyTextSection } from '../../SurveyTextSection';
import {
  ReferenceAnswerValue,
  Answers,
  Fields,
  PartialAnswer,
  AdditionalFilterOptionsFunc,
  AttributeAnswerValue,
  GetAISuggestions,
  FetchComponentOptionsWithQuery,
  GenerateComponentDescription,
} from '../../types';
import { DeleteReferenceButton } from '../ReferenceQuestions/DeleteReferenceButton';
import { SurveyReferenceAndDependentQuestionHeader } from '../SurveyReferenceAndDependentQuestionHeader';
import { EditReferenceButton } from '../ReferenceQuestions/EditReference';
import {
  getFieldType,
  getFieldTypeFromAttributeName,
} from '../../utils/fieldOperations';
import { getNameAnswer } from '../../utils/answerOperations';
import {
  isEmptyAnswer,
  isEmptyDateRangeAnswer,
  isAnsweredParentQuestion,
} from '../../utils/answerPredicates';
import { QuestionContentWrapper } from '../questionsTypes';

const isSubQuestionReadonly = (
  isParentQuestionReadonly: boolean,
  question: PersistedAttributeQuestion | PersistedFieldQuestion
) => {
  if (isParentQuestionReadonly) return true;
  return question.readonly ?? false;
};

type SurveyReferenceQuestionWithQuestionsProps = {
  survey: APISurveyAttributes;
  reference: ReferenceAnswerValue;
  questionId: string;
  questionLabel: string;
  componentId: ArdoqId | undefined;
  questions?: PersistedReferenceSubQuestion[];
  readonly: boolean;
  answers: Answers;
  initialCollapsed?: boolean;
  broadcastInstanceId?: string;
  fields: Fields;
  preventReferenceModification?: boolean | null;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  hasAttemptedSubmission: boolean;
  identifierFieldName: string | undefined;
  hasAutocompleteFeature: boolean;
  hasEditableReferencesInQuestionsFeature: boolean;
  isEmbedded: boolean;
  hideReadOnlyLabel: boolean;
  QuestionContentWrapper: QuestionContentWrapper;
  allowComponentCreation: boolean;
  highlightUnansweredPriorityQuestions: boolean;
  generateComponentDescription: GenerateComponentDescription;
  trackDescriptionGenerated: (value: AttributeAnswerValue) => void;
  trackDescriptionGeneratedFailed: () => void;
  deleteReference: () => void;
  setAnswer: (answer: PartialAnswer) => void;
  editReference: (componentOption: SelectOption<string>) => void;
  additionalFilterOptions?: AdditionalFilterOptionsFunc;
  getExistingComponentByName: (name: string) => void;
  getDuplicate: (value: string) => null | { path: string };
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
  userOptions: SelectOption<ArdoqId>[];
};

export const SurveyReferenceQuestionWithQuestions = ({
  survey,
  reference,
  questionId,
  componentId,
  questions = [],
  questionLabel,
  readonly,
  answers,
  fields,
  initialCollapsed,
  preventReferenceModification,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  hasAttemptedSubmission,
  identifierFieldName,
  hasAutocompleteFeature,
  hasEditableReferencesInQuestionsFeature,
  isEmbedded,
  hideReadOnlyLabel,
  QuestionContentWrapper,
  allowComponentCreation,
  highlightUnansweredPriorityQuestions,
  generateComponentDescription,
  trackDescriptionGenerated,
  trackDescriptionGeneratedFailed,
  deleteReference,
  setAnswer,
  getDuplicate,
  getExistingComponentByName,
  additionalFilterOptions,
  editReference,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  userOptions,
}: SurveyReferenceQuestionWithQuestionsProps) => {
  const [isCollapsed, setIsCollapsed] = useState(!!initialCollapsed);
  const requiredQuestions = questions
    .filter(q => q.validators && q.validators.some(v => v.type === 'required'))
    .map(toId);
  const answersNotNil = Object.entries(answers)
    .filter(([, e]) => {
      const value = e?.value;
      if (isEmptyAnswer(value)) return false;
      if (isDateRangeValue(value) && isEmptyDateRangeAnswer(value))
        return false;
      return true;
    })
    .map(([id]) => id);
  const requiredAnswersComplete = requiredQuestions.every(r =>
    answersNotNil.includes(r)
  );
  const { lock = false } = reference || {};
  const isLocked = typeof lock === 'string'; // Lock is a string id of who locked the component or null if there is no lock
  const componentName = getNameAnswer(questions, answers);
  const componentTypeName = survey.componentTypeName;
  const referenceName = reference.component.name;
  const identifierString = getIdentifierStringFromReferenceAnswerValue(
    reference,
    fields,
    identifierFieldName,
    currentLocale
  );
  const referenceNameWithIdentifier = `${referenceName} ${identifierString}`;
  const headerName = identifierFieldName
    ? referenceNameWithIdentifier
    : referenceName;
  return (
    <Box>
      <SurveyReferenceAndDependentQuestionHeader
        isCollapsed={isCollapsed}
        leftContent={headerName}
        requiredAnswersMissing={!requiredAnswersComplete}
        i18nLocale={i18nLocale}
        rightContent={
          hideReadOnlyLabel ? null : (
            <FlexBox marginLeft="auto">
              {!preventReferenceModification &&
                hasEditableReferencesInQuestionsFeature && (
                  <EditReferenceButton
                    surveyId={survey._id}
                    questionId={questionId}
                    questionLabel={questionLabel}
                    componentId={componentId}
                    isLocked={isLocked}
                    readonly={readonly}
                    currentLocale={currentLocale}
                    i18nLocale={i18nLocale}
                    fields={fields}
                    identifierFieldName={identifierFieldName}
                    allowComponentCreation={allowComponentCreation}
                    additionalFilterOptions={additionalFilterOptions}
                    editReference={editReference}
                    getAISuggestions={getAISuggestions}
                    fetchComponentOptionsWithQuery={
                      fetchComponentOptionsWithQuery
                    }
                  />
                )}
              <DeleteReferenceButton
                isLocked={isLocked}
                readonly={readonly}
                preventReferenceModification={Boolean(
                  preventReferenceModification
                )}
                i18nLocale={i18nLocale}
                deleteReference={deleteReference}
              />
            </FlexBox>
          )
        }
        setIsCollapsed={setIsCollapsed}
      />
      <SurveyReferenceAndDependentQuestionField
        $isDisabled={isLocked}
        $isCollapsed={isCollapsed}
      >
        <Stack gap="medium">
          {questions.map(question => {
            switch (question.type) {
              case SurveyQuestionType.TEXT:
                return (
                  <SurveyTextSection question={question} key={question._id} />
                );
              case SurveyQuestionType.ATTRIBUTE: {
                const { originalValue, validateDuplicateName, answer, value } =
                  setupAttributeQuestionData({
                    question,
                    answers,
                    isSaving: false,
                    defaultParentId: survey.newComponentParentId,
                  });
                return (
                  <SurveyAttributeQuestion
                    userOptions={userOptions}
                    surveyId={survey._id}
                    question={question}
                    answer={answer}
                    componentId={componentId}
                    readonly={isSubQuestionReadonly(readonly, question)}
                    originalValue={originalValue}
                    validateDuplicateName={validateDuplicateName}
                    isLocked={isLocked}
                    componentName={componentName}
                    componentTypeName={componentTypeName}
                    key={question._id}
                    isReference
                    value={value}
                    hasAttemptedSubmission={hasAttemptedSubmission}
                    currentLocale={currentLocale}
                    i18nLocale={i18nLocale}
                    hasAutocompleteFeature={hasAutocompleteFeature}
                    type={getFieldTypeFromAttributeName(
                      question.properties.attributeName
                    )}
                    isEmbedded={isEmbedded}
                    hideReadOnlyLabel={hideReadOnlyLabel}
                    QuestionContentWrapper={QuestionContentWrapper}
                    highlightUnansweredPriorityQuestions={
                      highlightUnansweredPriorityQuestions
                    }
                    generateComponentDescription={generateComponentDescription}
                    onDescriptionReceived={() =>
                      trackDescriptionGenerated(value)
                    }
                    onLLMCouldNotGenerateDescription={
                      trackDescriptionGeneratedFailed
                    }
                    parentName={
                      isAnsweredParentQuestion(answer)
                        ? answer.parentName
                        : undefined
                    }
                    getExistingComponentByName={getExistingComponentByName}
                    setAnswer={setAnswer}
                    getDuplicate={getDuplicate}
                    getAISuggestions={getAISuggestions}
                    fetchComponentOptionsWithQuery={
                      fetchComponentOptionsWithQuery
                    }
                  />
                );
              }
              case SurveyQuestionType.FIELD: {
                const type = getFieldType(
                  question.properties.fieldName,
                  fields
                );
                if (isDateRangeFieldType(type)) {
                  const { value, startDate, endDate, answer } =
                    setupDateRangeFieldQuestionData({
                      fields,
                      question,
                      answers,
                    });
                  return (
                    <SurveyDateRangeFieldQuestion
                      question={question}
                      answer={answer}
                      readonly={isSubQuestionReadonly(readonly, question)}
                      isLocked={isLocked}
                      startDateField={startDate}
                      endDateField={endDate}
                      key={question._id}
                      value={value}
                      currentLocale={currentLocale}
                      i18nLocale={i18nLocale}
                      hideReadOnlyLabel={hideReadOnlyLabel}
                      QuestionContentWrapper={QuestionContentWrapper}
                      highlightUnansweredPriorityQuestions={
                        highlightUnansweredPriorityQuestions
                      }
                      setAnswer={setAnswer}
                    />
                  );
                }
                const {
                  field,
                  originalValue,
                  validateDuplicateName,
                  value,
                  answer,
                } = setupFieldQuestionData({
                  fields,
                  question,
                  answers,
                  isSaving: false,
                });
                return (
                  <SurveyFieldQuestion
                    userOptions={userOptions}
                    surveyId={survey._id}
                    question={question}
                    answer={answer}
                    componentId={componentId}
                    readonly={isSubQuestionReadonly(readonly, question)}
                    originalValue={originalValue}
                    type={type}
                    field={field}
                    validateDuplicateName={validateDuplicateName}
                    isLocked={isLocked}
                    componentName={componentName}
                    componentTypeName={componentTypeName}
                    key={question._id}
                    isReference
                    value={value}
                    hasAttemptedSubmission={hasAttemptedSubmission}
                    currentLocale={currentLocale}
                    i18nLocale={i18nLocale}
                    hasAutocompleteFeature={hasAutocompleteFeature}
                    isEmbedded={isEmbedded}
                    hideReadOnlyLabel={hideReadOnlyLabel}
                    QuestionContentWrapper={QuestionContentWrapper}
                    highlightUnansweredPriorityQuestions={
                      highlightUnansweredPriorityQuestions
                    }
                    getExistingComponentByName={getExistingComponentByName}
                    setAnswer={setAnswer}
                    getDuplicate={getDuplicate}
                    getAISuggestions={getAISuggestions}
                    fetchComponentOptionsWithQuery={
                      fetchComponentOptionsWithQuery
                    }
                    generateComponentDescription={generateComponentDescription}
                  />
                );
              }
              default:
                return exhaustiveCheck(question);
            }
          })}
        </Stack>
      </SurveyReferenceAndDependentQuestionField>
    </Box>
  );
};
